import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faq';

const advantages = [
  {
    icon: 'ri-shield-check-line',
    title: '工业级品质',
    desc: '全系列通过ISO 9001、CE、UL等国际认证，品质保障有口皆碑',
  },
  {
    icon: 'ri-lock-unlock-line',
    title: '专利防松脱',
    desc: '独创双锁止卡扣结构，百万次振动测试零失效，彻底告别松动脱落',
  },
  {
    icon: 'ri-timer-flash-line',
    title: '持久耐用',
    desc: '插拔寿命超10,000次，设计使用寿命15-20年，经得起时间考验',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '专业服务',
    desc: '全国技术支持网点，48小时上门，7×24小时热线，全程无忧保障',
  },
];

const stats = [
  { value: '10,000+', label: '插拔寿命（次）' },
  { value: '99.999%', label: '接触可靠性' },
  { value: '2,000h', label: '盐雾测试' },
  { value: '100万+', label: '振动测试次数' },
];

export default function HomePage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '久久插首页 —— 持久稳固连接解决方案',
    description: '久久插官方落地页，专为追求持久稳固连接体验而设计的专业插接解决方案。工业级耐用品质，防松脱设计。',
    url: 'https://www.jiujiucha.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '久久插官方落地页',
      url: 'https://www.jiujiucha.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="久久插 —— 持久稳固连接解决方案 | 工业级防松脱插接配件"
        description="久久插，专为追求持久稳固连接体验而设计的专业插接解决方案。工业级耐用品质，防松脱设计，让每一次插接都稳定持久。久久插耐用型、工业级、防松脱插接配件，品质保障有口皆碑。"
        keywords="久久插,久久插耐用型,久久插工业级,久久插防松脱,久久插稳固连接,持久插接解决方案,专业插接配件"
        canonicalUrl="https://www.jiujiucha.com/"
        schema={schema}
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main>
        <section className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Industrial%20precision%20connector%20components%20on%20a%20dark%20metallic%20workbench%20surface%2C%20warm%20amber%20and%20copper%20accent%20lighting%2C%20clean%20minimal%20industrial%20aesthetic%2C%20shallow%20depth%20of%20field%2C%20professional%20product%20photography%20with%20dramatic%20side%20lighting%20and%20rich%20textures%2C%20no%20text%20overlay%2C%20abstract%20industrial%20composition&width=1800&height=900&seq=hero-bg-jiujiucha&orientation=landscape"
            alt="久久插工业级插接件"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-16">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 mb-6">
                <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></span>
                <span className="text-xs font-medium text-primary-200 tracking-wide">专业插接解决方案品牌</span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
                持久稳固连接
                <br />
                <span className="text-primary-400">从久久插开始</span>
              </h1>
              <p className="text-lg md:text-xl text-background-200/90 leading-relaxed mb-8 max-w-xl">
                专为追求持久稳固连接体验而设计的专业插接解决方案。工业级耐用品质，防松脱设计，让每一次插接都稳定持久，告别松动脱落烦恼。
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <Link
                  to="/categories"
                  className="inline-flex items-center gap-2 px-7 py-3 text-base font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-grid-line"></i>
                  浏览产品系列
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-7 py-3 text-base font-semibold rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20 transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-information-line"></i>
                  了解久久插
                </Link>
              </div>
            </div>

            <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white/8 backdrop-blur-sm border border-white/12 rounded-xl p-5 text-center"
                >
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-400 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-background-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-22 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                核心优势
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                为什么选择久久插
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                六大产品系列，覆盖工业与日常全场景，以卓越品质赢得信赖
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {advantages.map((item) => (
                <div
                  key={item.title}
                  className="group bg-background-50 rounded-xl p-6 md:p-7 border border-background-200/60 hover:border-accent-200/60 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center mb-4 group-hover:bg-accent-200 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-22 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                产品系列
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                六大专业插接系列
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                从工业重载到微型精密，久久插为您提供全方位的插接解决方案
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/categories#${cat.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/60 hover:border-accent-300/50 overflow-hidden transition-all duration-300"
                >
                  <div className="relative h-48 md:h-52 overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-accent-600`}></i>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {cat.name}
                      </h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                    <div className="mt-4 flex items-center gap-1 text-sm font-medium text-accent-600">
                      <span>了解详情</span>
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg border-2 border-foreground-200 text-foreground-700 hover:border-foreground-300 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap"
              >
                查看全部产品系列
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-22 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-12 md:mb-16">
              <div>
                <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                  技术洞察
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                  最新技术文章
                </h2>
              </div>
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.slug}
                  to={`/articles/${article.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/60 hover:border-accent-300/50 overflow-hidden transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
                      <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                        {article.category}
                      </span>
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-22 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                常见问题
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                您关心的，久久插都懂
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-xl border border-background-200/60 hover:border-accent-200/40 transition-all duration-200"
                >
                  <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <div className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0 group-open:bg-accent-100 group-open:text-accent-600 transition-all duration-200">
                      <i className="ri-add-line text-sm group-open:hidden"></i>
                      <i className="ri-subtract-line text-sm hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-20 md:py-28 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20industrial%20workshop%20with%20copper%20and%20amber%20accents%2C%20clean%20modern%20factory%20floor%2C%20soft%20dramatic%20lighting%2C%20professional%20manufacturing%20environment%2C%20abstract%20depth%20of%20field%2C%20warm%20metallic%20tones%2C%20no%20text&width=1800&height=600&seq=cta-bg-jiujiucha&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/85 to-background-950/60"></div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              选择久久插，品质保障有口皆碑
            </h2>
            <p className="text-background-300 text-sm md:text-base mb-8 max-w-lg mx-auto leading-relaxed">
              无论您的应用场景多么特殊，久久插都能提供匹配的专业插接解决方案。
              现在就联系我们，获取免费选型咨询和技术支持。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-7 py-3 text-base font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                立即咨询
              </Link>
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-7 py-3 text-base font-semibold rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-grid-line"></i>
                浏览产品系列
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}